import { defineComponent as _defineComponent } from 'vue'
import GBaseModal from '@/components/g-base-modal.vue'
import { showSyncModal } from '@/components/syncmodal'
import { translateLang } from '@/data/i18n/translateLang'
import { useEventBus, useWindowEvent } from '@/hooks/use-event'
import EventBus, { EventBusKey } from '@/util/bus'
import { message } from 'ant-design-vue'
import { nextTick, reactive, ref, watch } from 'vue'

import MFolderItem from './MFolderItem.vue'
import MProcessFolder from './MProcessFolder.vue'
import MProcessingFileItem from './MProcessingFileItem.vue'
import { defaultProcessFiles, getDefaultFolder } from './data'
import MFolderLegend from './m-folder-legend.vue'
import { CustomFolderDragEvent, File, FileType, Folder } from './types'
import { FolderAuthority } from './utils/folderAuthority'
import { FolderTreeManager } from './utils/folderControl'

type EventBusKeyType = keyof typeof EventBusKey

/** @description 文件夹树管理器 */

export default /*#__PURE__*/_defineComponent({
  __name: 'MFolderTagsSection',
  props: {
    customizeFolderConfig: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  /** 接口存储的用户文件夹设置 */
  customizeFolderConfig: Folder
}

const folderManage: FolderTreeManager = new FolderTreeManager(
  getDefaultFolder()
)


/** 自定义文件夹树 */
const customFolderTrees = ref<Folder>(getDefaultFolder())

/** 当前点击活跃的文件夹/文件 ID */
const activeId = ref<string>()
/** 当前可以被修改操作的文件夹-弹窗修改信息/新建 */
const editableFolder = ref<Folder>()
/** 记录当前正在被拖拽的文件/文件夹 */
const draggableData = ref<File | Folder>()

const processFolderRef = ref<InstanceType<typeof MProcessFolder>>()

/** 可以拖动的加工文件列表 */
const processingFiles = ref(
  window.$copy(defaultProcessFiles) as typeof defaultProcessFiles
)
/** 已被添加过的文件类型 */
const usedFileTypes = ref<FileType[]>([])

/** 弹窗状态记录 */
const modalStatus = reactive({
  /** 是否正在删除文件夹/文件 */
  isDeleting: false,
})

/** @description 确认修改/添加文件夹 */
function handleConfirmEditFolder(data: Folder, model: 'add' | 'edit') {
  if (!data.name) {
    message.error(translateLang('文件夹名称不能为空'))
    return
  }
  if (model === 'add') {
    if (folderManage.isExistSameNameFile(data, data.pid)) {
      message.error(translateLang('存在同名文件夹'))
      return
    }
    folderManage.createFolder(data)
  } else {
    // 根目录直接更新
    if (!folderManage.isRoot(data.id)) {
      const parentNode = folderManage.getEntityById(data.pid) as Folder
      // 先排除自身
      const childrenNames = parentNode.children
        .filter((v) => v.id !== data.id)
        .map((v) => v.name)
      if (childrenNames.includes(data.name)) {
        message.error(translateLang('存在同名文件夹'))
        return
      }
    }
    folderManage.updateFolderNode(data.id, data)
  }
  validateFolderTree()
  processFolderRef.value?.close()
}

/** @description 添加单个加工文件到文件夹 */
function addFile(data: CustomFolderDragEvent) {
  const { file } = data
  const processFile = defaultProcessFiles
    .map((v) => v.files)
    .flat(1)
    .find((file) => file.type === file.type)
  if (!processFile) return message.error(translateLang('未能获取到正确的文件'))
  if (folderManage.hasFileOfType(file.pid, file.type))
    return message.error(
      translateLang('【{name}】在此文件夹中已存在', { name: file.name })
    )
  if (folderManage.has(file.id)) {
    folderManage.moveFile(file)
    validateFolderTree()
    return
  }
  folderManage.createFile(file)
  validateFolderTree()
  resetStatus()
}

/** @description 添加新的文件夹 */
function addFolder(data: CustomFolderDragEvent) {
  if (folderManage.has(data.folder.id)) {
    folderManage.moveFolder(data.folder)
    validateFolderTree()
    return
  }
  editableFolder.value = data.folder
  nextTick(() => {
    processFolderRef.value?.setModel('add')
    processFolderRef.value?.open()
  })
}
/** @description 修改文件夹 */
function editFolder(data: CustomFolderDragEvent) {
  editableFolder.value = data.folder
  nextTick(() => {
    processFolderRef.value?.setModel('edit')
    processFolderRef.value?.open()
  })
}
/** @description 删除文件夹 */
async function deleteFolderFile(data: CustomFolderDragEvent) {
  try {
    const { file, folder, type } = data
    modalStatus.isDeleting = true
    let content =
      type === 'delete-folder'
        ? translateLang('是否确定删除该文件夹？')
        : translateLang('是否确定删除【{name}】？', {
            name: file?.name || folder?.name,
          })
    if (folder) {
      const len = folder.children.length || folder.accept.supportedFiles.length
      if (len > 0) {
        content = translateLang('此文件夹下存在其他文件夹/文件，是否确定删除？')
      }
    }
    const { status } = await showSyncModal({
      isUseKeyboard: true,
      modalComponent: GBaseModal,
      contain: content,
    } as any)
    if (!status) return
    const id = folder?.id || file?.id
    switch (type) {
      case 'delete-folder':
        folderManage.deleteFolder(id)
        break
      case 'delete-file':
        folderManage.deleteFile(id)
        break
    }
    resetStatus()
  } finally {
    modalStatus.isDeleting = false
  }
}

/** @description 激活文件夹/文件 */
function activeFolderFile(data: CustomFolderDragEvent) {
  activeId.value = data.id
}

/** @description 拖动开始 */
function dragStart(data: CustomFolderDragEvent) {
  draggableData.value = data.file || data.folder
}

/** @description 获取文件夹树 */
function getFolderTree() {
  return window.$copy(customFolderTrees.value)
}
/** @description 待文件树发生改变时需要全局重置的部分状态 */
function resetStatus() {
  usedFileTypes.value = folderManage.getAllFileTypes()
}

/** @description 校验文件树内文件分类权限 */
function validateFolderTree() {
  FolderAuthority.validateFolderTree(customFolderTrees.value)
}

/** @description 重置文件夹模板 */
async function handleResetFolderTemplate() {
  const { status } = await showSyncModal({
    isUseKeyboard: true,
    modalComponent: GBaseModal,
    contain: translateLang('是否确认重置模板?'),
    okText: translateLang('确认'),
  } as any)
  if (!status) return
  customFolderTrees.value = getDefaultFolder()
  folderManage.rebuildTree(customFolderTrees.value)
  resetStatus()
  validateFolderTree()
}

/** @description 清空模板 */
async function handleClearFolderTemplate() {
  const { status } = await showSyncModal({
    isUseKeyboard: true,
    modalComponent: GBaseModal,
    contain: translateLang('是否确认清空模板?'),
    okText: translateLang('确认'),
  } as any)
  if (!status) return
  const defaultFolder = getDefaultFolder()
  Reflect.deleteProperty(defaultFolder, 'children')
  defaultFolder.children = []
  customFolderTrees.value = defaultFolder
  folderManage.rebuildTree(customFolderTrees.value)
  resetStatus()
  validateFolderTree()
}
/** 监听文件夹触发的相关事件 */
useEventBus(
  EventBusKey.CUSTOM_FOLDER_DRAG_EVENT as EventBusKeyType,
  (event: CustomFolderDragEvent) => {
    const eventMap = {
      'add-file': addFile,
      'add-folder': addFolder,
      'edit-folder': editFolder,
      'delete-folder': deleteFolderFile,
      'delete-file': deleteFolderFile,
      active: activeFolderFile,
      'drag-start': dragStart,
    }
    eventMap[event.type]?.(event)
  }
)

/** 监听翻译变化 */
useEventBus(EventBusKey.TRANSLATE as EventBusKeyType, validateFolderTree)

/** @description 键盘事件监听 */
useWindowEvent('keydown', (e) => {
  if (e.key === 'Delete' && activeId.value) {
    // 根节点不能删除
    if (folderManage.isRoot(activeId.value)) return
    // 可能是文件和文件夹
    const active = folderManage.getEntityById(activeId.value) as Folder
    const eventObj: Partial<CustomFolderDragEvent> = {
      type: active.isFolder ? 'delete-folder' : 'delete-file',
    }
    if (active.isFolder) {
      eventObj.folder = active
    } else {
      eventObj.file = active as unknown as File
    }
    if (modalStatus.isDeleting) return
    EventBus.$emit(EventBusKey.CUSTOM_FOLDER_DRAG_EVENT, eventObj)
  }
})

/** 数据更新同步更新文件夹树 */
watch(
  () => props.customizeFolderConfig,
  (newVal) => {
    customFolderTrees.value = Object.keys(newVal).length
      ? newVal
      : getDefaultFolder()
    folderManage.rebuildTree(customFolderTrees.value)
    resetStatus()
    validateFolderTree()
  },
  {
    immediate: true,
  }
)

expose({
  getFolderTree,
})

return { __sfc: true,folderManage, props, customFolderTrees, activeId, editableFolder, draggableData, processFolderRef, processingFiles, usedFileTypes, modalStatus, handleConfirmEditFolder, addFile, addFolder, editFolder, deleteFolderFile, activeFolderFile, dragStart, getFolderTree, resetStatus, validateFolderTree, handleResetFolderTemplate, handleClearFolderTemplate, translateLang, MFolderItem, MProcessFolder, MProcessingFileItem, MFolderLegend }
}

})